/**
 * Generate FAQ JSON-LD for the Common Crawl Rank Checker page.
 * Used for SEO / structured data.
 *
 * This is a server-safe function (no "use client" directive).
 */
export function generateCommonCrawlRankCheckerFaqJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What does this tool check?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "This tool looks up a domain in Common Crawl's web graph and reports its Harmonic Centrality rank, its PageRank, and where it sits in the overall graph. It also shows an authority grade from A to F and a full AI Placement Value Score, so you can see how authoritative AI models are likely to treat the site."
        }
      },
      {
        "@type": "Question",
        "name": "What is Harmonic Centrality?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Harmonic Centrality measures how central a domain is in the web's link graph, based on how easily it can be reached from every other site through short link paths. A high Harmonic Centrality means many well-connected sites are only a few links away from you. It is harder to game than raw link counts, so it is a strong signal of real authority."
        }
      },
      {
        "@type": "Question",
        "name": "Why does Common Crawl rank matter for AI visibility?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Common Crawl is one of the largest sources of training data for large language models. It uses Harmonic Centrality to decide which sites to crawl most, so high-centrality domains appear more often in the data AI models learn from. That gives them a baseline familiarity and authority in AI answers, before any real-time search even happens."
        }
      },
      {
        "@type": "Question",
        "name": "Why do you only cover the top 10 million domains?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Common Crawl's full web graph contains hundreds of millions of domains. We load the top 10 million by Harmonic Centrality, which covers the sites with meaningful authority and web presence. If a domain is not in the top 10 million, we report it as unranked, which still tells you something useful: there is room to build its standing in the graph."
        }
      },
      {
        "@type": "Question",
        "name": "How is the authority grade calculated?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The grade is based on a domain's Harmonic Centrality rank. Grade A is the top 1,000 domains, B is the top 10,000, C is the top 100,000, D is the top 1,000,000, and E covers the rest of the tracked top 10,000,000. Domains outside the tracked set receive an F. The buckets are log-scale, so each grade is roughly a tenfold difference in centrality."
        }
      },
      {
        "@type": "Question",
        "name": "How can I improve my domain's rank?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Earn links and brand mentions from sites that are themselves deeply embedded in the web's core, such as major publishers and institutions. Link topology beats link volume: one link from a highly central site can move your Harmonic Centrality more than dozens from isolated pages. Keeping an accurate Wikidata entity and Wikipedia presence, and running earned media that produces authoritative coverage, all help over time."
        }
      },
      {
        "@type": "Question",
        "name": "How does this relate to the AI Placement Value Score?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The AI Placement Value Score combines a domain's organic authority (which includes its PageRank standing from this same Common Crawl data), how accessible it is to AI crawlers, and how much influence it carries in AI training data. Your Common Crawl rank is one of the core inputs, so a strong rank tends to lift the placement value of coverage earned on that domain."
        }
      },
      {
        "@type": "Question",
        "name": "Is this data live?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We refresh the dataset each time Common Crawl publishes a new web graph release. The results page shows which release is loaded, how many domains are tracked, and the date it was last updated, so you always know how current the numbers are."
        }
      }
    ]
  };
}
